import {
  ConfirmationDialog,
  DeleteDialogV2,
  ErpDataTableV2,
  ErpInputTableV2,
  BulkDeleteDialog
} from '@/components/custom/erp';
import { useForm, useHoaDonBanDichVuDetail, useSearchDialog, useSearchFieldStates, useTableData } from './hooks';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { HoaDonBanDichVuSearchProvider, useHoaDonBanDichVuSearchContext } from './context';
import { type HoaDonBanHangDichVu, type HoaDonBanHangDichVuInput } from '@/types/schemas';
import { getDataTableColumns, getInputTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';

import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

function HoaDonBanDichVuPageContent() {
  const {
    data: hoaDonBanDichVus,
    isLoading,
    currentPage,
    totalItems,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    sumTotal
  } = useCRUD<HoaDonBanHangDichVu, HoaDonBanHangDichVuInput>({
    endpoint: QUERY_KEYS.HOA_DON_BAN_DICH_VU,
    pageSize: 20,
    sumField: 't_tt_nt'
  });

  const { handleSearchChange, searchState, isExporting } = useHoaDonBanDichVuSearchContext();
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    showCantEdit,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    clearSelection,
    setShowCantEdit,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    showBulkDelete,
    handleCloseBulkDelete
  } = useForm(fetchDetail);

  const { state: searchFieldState, actions: searchFieldActions } = useSearchFieldStates();
  const { showSearchDialog, handleSearch, closeSearchDialog } = useSearchDialog();

  const handleSearchSubmit = (filters: BanHangPayload) => {
    handleSearchChange(filters);
    closeSearchDialog();
  };

  const { deleteItem: deleteGiayBaoCo } = useCRUD<BanHangRecord, BanHangPayload>({
    endpoint: QUERY_KEYS.GIAY_BAO_CO,
    initialFetch: false
  });
  const { deleteItem: deletePhieuThu } = useCRUD<BanHangRecord, BanHangPayload>({
    endpoint: QUERY_KEYS.PHIEU_THU,
    initialFetch: false
  });

  const hasFilterParams = searchState.filterParams && Object.keys(searchState.filterParams).length > 0;
  const displayData = hasFilterParams ? searchState.searchResults : hoaDonBanDichVus;

  const { tables } = useTableData(displayData, handleViewClick, sumTotal, isLoading);

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      if (formMode === 'add') {
        await addItem(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, { ...data, so_ct: selectedObj.so_ct, i_so_ct: selectedObj.i_so_ct });

        // Trước đó có tick chi tiền -> Bỏ tick -> Xóa giấy báo có
        if (
          !data.pt_tao_yn &&
          selectedObj.ma_httt === 'CKB' &&
          selectedObj.pt_tao_yn &&
          selectedObj.giay_bao_co_uuids[0]
        )
          await deleteGiayBaoCo(selectedObj.giay_bao_co_uuids[0]);
        // Trước đó có tick chi tiền -> Bỏ tick -> Xóa phiếu thu
        else if (
          !data.pt_tao_yn &&
          selectedObj.ma_httt === 'TMB' &&
          selectedObj.pt_tao_yn &&
          selectedObj.phieu_thu_uuids[0]
        )
          await deletePhieuThu(selectedObj.phieu_thu_uuids[0]);
      }
      handleCloseForm();
      clearSelection();
      refreshData();
    } catch (error) {
      return;
    }
  };

  return (
    <div className='flex h-screen w-screen flex-col overflow-hidden'>
      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshData();
              if (selectedObj) await fetchDetail(selectedObj.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
            <ResizablePanelGroup direction='vertical' className='h-full overflow-hidden'>
              <ResizablePanel defaultSize={55} minSize={20}>
                <div className='h-full w-full overflow-hidden'>
                  {!isExporting ? (
                    <ErpDataTableV2
                      tables={tables}
                      onRowClick={handleRowClick}
                      selectedRowId={selectedRowIndex || undefined}
                      totalItems={totalItems}
                      currentPage={currentPage}
                      onSearchChange={handleSearchChange as BanHangRecord}
                      onPageChange={handlePageChange}
                      pageSize={20}
                      loading={isLoading}
                    />
                  ) : null}
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle className='bg-slate-200/80 hover:bg-slate-300' />

              <ResizablePanel defaultSize={45} minSize={20}>
                <div className='h-full overflow-hidden'>
                  <ErpInputTableV2
                    rows={detail?.chi_tiet || []}
                    columns={getInputTableColumns()}
                    showIndexColumn
                    actionsDisabled={formMode === 'view' || !selectedObj}
                    height='100%'
                    loading={isLoading && !selectedObj}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </>
      )}
      {showForm && (
        <FormDialog
          formMode={formMode}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
        />
      )}

      {showCantEdit && (
        <ConfirmationDialog
          onClose={() => setShowCantEdit(false)}
          onConfirm={() => setShowCantEdit(false)}
          message='Hóa đơn đã thanh toán, không thể sửa/xóa'
          showCancelBtn={false}
        />
      )}

      {showDelete && (
        <DeleteDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          deleteObj={deleteItem}
          selectedObj={selectedObj}
          clearSelection={clearSelection}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Hoá đơn bán dịch vụ'
          onClose={handleCloseBulkDelete}
          columns={getDataTableColumns(handleViewClick)}
          dataRows={displayData}
          handleDeleteMany={uuids => {
            void Promise.all(uuids.map(uuid => deleteItem(uuid)));
          }}
        />
      )}

      {showSearchDialog && (
        <SearchDialog
          open={showSearchDialog}
          onClose={closeSearchDialog}
          onSearch={handleSearchSubmit}
          searchParams={searchState.filterParams}
          searchFieldState={searchFieldState}
          searchFieldActions={searchFieldActions}
        />
      )}
    </div>
  );
}

export default function HoaDonBanDichVuPage() {
  return (
    <HoaDonBanDichVuSearchProvider>
      <HoaDonBanDichVuPageContent />
    </HoaDonBanDichVuSearchProvider>
  );
}
